import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Runs a promise and reports which of the four states the screen is in.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * Every screen in this app currently reads its data straight out of
 * `src/data/*.ts` - synchronously, always present, never failing. Nothing had
 * a loading state because nothing could ever be loading.
 *
 * The moment any of that comes from a server, one line becomes four cases, and
 * writing those four by hand on every screen is how they end up subtly
 * different: one screen shows a spinner on refresh, another blanks the list;
 * one treats an error as empty, another crashes on `data.map`.
 *
 * ---------------------------------------------------------------------------
 * THE DISTINCTION THAT MATTERS MOST
 * ---------------------------------------------------------------------------
 * `loading` and `refreshing` are NOT the same thing, and collapsing them is
 * the most common way a list-driven app feels broken:
 *
 *   loading     nothing on screen yet  ->  draw skeletons
 *   refreshing  data already on screen ->  KEEP it, spin the pull-to-refresh
 *
 * Blanking a list back to skeletons on every pull-to-refresh throws away what
 * the customer was reading and makes a 200ms request look like a page reload.
 *
 * Likewise `error` does not clear `data`. A failed refresh should leave the
 * last good list on screen with the failure reported above it, not replace
 * something true with nothing.
 */

export type AsyncStatus = 'loading' | 'refreshing' | 'ready' | 'error';

export type Async<T> = {
  data: T | null;
  error: Error | null;
  status: AsyncStatus;
  /** First load, nothing to show yet. This is the one that draws skeletons. */
  loading: boolean;
  /** Reloading over data that is already on screen. Keep the content. */
  refreshing: boolean;
  /**
   * The request worked and came back with nothing.
   *
   * Only ever true on a successful response - which is exactly what makes it
   * safe to draw an `EmptyState` from. A failed request is never "empty".
   */
  isEmpty: boolean;
  /** Run it again. Safe to pass straight to a Retry button or `onRefresh`. */
  reload: () => void;
};

type Options = {
  /**
   * Hold off until this is true - for a request that needs a token, an id, or
   * an address the user has not chosen yet.
   *
   * Stays in `loading` while false rather than reporting ready-with-nothing,
   * because "not asked yet" and "asked, got nothing" are different facts and
   * only the second one should draw an empty state.
   */
  enabled?: boolean;
};

/** True for `[]`, `{}` and `null` - the three shapes "nothing" arrives in. */
const emptyish = (value: unknown): boolean => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export function useAsync<T>(
  run: () => Promise<T>,
  deps: unknown[] = [],
  { enabled = true }: Options = {},
): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<AsyncStatus>('loading');

  /*
   * Which request is the current one.
   *
   * Two requests can be in flight at once - a filter changed while the first
   * was still going - and they can land out of order. Without this, the slower
   * earlier request overwrites the newer one and the screen shows the results
   * of a filter the customer has already moved off.
   */
  const attempt = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /* `run` is nearly always an inline arrow, so it is a new function every
     render. Depending on it directly would re-fire the request on every
     render; the caller's `deps` are what actually decide when to refetch. */
  const runRef = useRef(run);
  runRef.current = run;

  const load = useCallback(async () => {
    if (!enabled) {
      return;
    }

    const id = ++attempt.current;

    // Refreshing only when there is something to keep on screen. `setStatus`
    // reads the previous data through the updater, so this does not need
    // `data` in the dependency list and cannot go stale.
    setStatus(current => (current === 'ready' ? 'refreshing' : 'loading'));

    try {
      const result = await runRef.current();
      if (!alive.current || id !== attempt.current) {
        return;
      }
      setData(result);
      setError(null);
      setStatus('ready');
    } catch (thrown) {
      if (!alive.current || id !== attempt.current) {
        return;
      }
      // The data is deliberately left alone - see the note at the top.
      setError(
        thrown instanceof Error ? thrown : new Error(String(thrown)),
      );
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    // The promise is deliberately unawaited: `load` reports its own outcome
    // through state and never rejects, so there is nothing here to catch.
    load().catch(() => {});
  }, [load]);

  return {
    data,
    error,
    status,
    // An error with nothing behind it is not "loading", and an error with the
    // last good list behind it is not either - both are handled by the screen.
    loading: status === 'loading',
    refreshing: status === 'refreshing',
    isEmpty: status === 'ready' && emptyish(data),
    reload: () => {
      load().catch(() => {});
    },
  };
}

export default useAsync;
